import { Component, DestroyRef, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/** Le nom du bundle principal (« main-HB4UNUPO.js ») dans un index.html, `null` s'il n'y en a pas. */
export function bundlePrincipal(html: string | null | undefined): string | null {
  return /main-[A-Za-z0-9]+\.js/.exec(html ?? '')?.[0] ?? null;
}

/**
 * Bandeau « Une nouvelle version est disponible » (07/10/2026).
 *
 * Un onglet du back-office resté ouvert garde le code chargé à l'ouverture : après un
 * déploiement, l'opérateur travaille sur l'ancienne version sans le savoir (vu chez HET :
 * correctif en prod à 21h03, onglet ouvert à 20h39, « ça ne marche toujours pas »).
 *
 * Chaque build change le nom du bundle principal : on compare celui de la page à celui
 * de l'index.html servi maintenant (toutes les 5 min et au retour sur l'onglet). En
 * développement (`main.js` sans empreinte), rien n'est surveillé.
 */
@Component({
  selector: 'app-nouvelle-version',
  standalone: true,
  template: `
    @if (disponible()) {
      <div class="nouvelle-version" role="status">
        <i class="fa-light fa-arrows-rotate" aria-hidden="true"></i>
        <span>Une nouvelle version du back-office est disponible.</span>
        <button type="button" class="nouvelle-version__bouton" (click)="recharger()">Recharger</button>
      </div>
    }
  `,
  styles: [`
    .nouvelle-version { position: fixed; left: 50%; bottom: 20px; transform: translateX(-50%); z-index: 10000;
                        display: flex; align-items: center; gap: .75rem; padding: .7rem 1rem; border-radius: 10px;
                        background: var(--primary-color); color: var(--primary-color-text, #fff);
                        box-shadow: 0 8px 24px rgba(0, 0, 0, .2); font-weight: 600; max-width: calc(100vw - 32px); }
    .nouvelle-version__bouton { border: 0; border-radius: 6px; padding: .4rem .8rem; cursor: pointer; font-weight: 700;
                                background: var(--primary-color-text, #fff); color: var(--primary-color); }
  `],
})
export class NouvelleVersionComponent implements OnInit {
  private readonly navigateur = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);

  readonly disponible = signal(false);
  private courant: string | null = null;

  ngOnInit(): void {
    if (!this.navigateur) return;
    const script = Array.from(document.scripts).map((s) => s.getAttribute('src') ?? '').find((src) => /main-[A-Za-z0-9]+\.js/.test(src));
    this.courant = bundlePrincipal(script);
    if (!this.courant) return;

    const minuterie = setInterval(() => this.verifier(), 5 * 60 * 1000);
    const auRetour = () => { if (document.visibilityState === 'visible') this.verifier(); };
    document.addEventListener('visibilitychange', auRetour);
    this.destroyRef.onDestroy(() => {
      clearInterval(minuterie);
      document.removeEventListener('visibilitychange', auRetour);
    });
  }

  async verifier(): Promise<void> {
    if (this.disponible() || !this.courant) return;
    try {
      const reponse = await fetch(new URL('index.html', document.baseURI).toString(), { cache: 'no-store' });
      if (!reponse.ok) return;
      const servi = bundlePrincipal(await reponse.text());
      if (servi && servi !== this.courant) this.disponible.set(true);
    } catch {
      /* hors ligne ou serveur en maintenance : on réessaiera */
    }
  }

  recharger(): void {
    location.reload();
  }
}
